import type { Permission } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { useSession } from '../lib/session.js'
import { useTRPC } from '../lib/trpc.js'
import type { IconName } from './Icon.js'

export interface NavItem {
  to: string
  label: string
  icon: IconName
  permission?: Permission
}

/**
 * A rail label is the name of the page it opens. "Board" is what the
 * dispatchers call the schedule, and "Schedule" was only ever the route's
 * name. "Money" opened a page titled Invoices, so it now says Invoices.
 *
 * The field app has its own tabs (Today, Jobs, Team, Inbox, Me) and lists
 * whatever of this it does not draw as a tab inside Me.
 */
export const NAV: NavItem[] = [
  { to: '/', label: 'Today', icon: 'today' },
  { to: '/jobs', label: 'Jobs', icon: 'jobs', permission: 'JOB_VIEW' },
  { to: '/board', label: 'Board', icon: 'schedule', permission: 'SCHEDULE_VIEW' },
  { to: '/inbox', label: 'Inbox', icon: 'inbox', permission: 'INBOX_VIEW' },
  { to: '/tasks', label: 'Tasks', icon: 'board', permission: 'TASK_MANAGE' },
  { to: '/clients', label: 'Clients', icon: 'clients', permission: 'CLIENT_VIEW' },
  { to: '/agreements', label: 'Agreements', icon: 'agreement', permission: 'CLIENT_VIEW' },
  // Quotes and Invoices were two lists of documents beside the Jobs stages they
  // doubled; Billing is one screen read from the job's stage. Decision 111.
  { to: '/billing', label: 'Billing', icon: 'invoices', permission: 'INVOICE_VIEW' },
  { to: '/materials', label: 'Catalog', icon: 'catalog', permission: 'CATALOG_VIEW' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
]

export function navItemIsActive(item: Pick<NavItem, 'to'>, pathname: string): boolean {
  if (item.to === '/') return pathname === '/'
  if (item.to === '/settings') {
    return pathname.startsWith('/settings') || pathname.startsWith('/people/')
  }
  return pathname.startsWith(item.to)
}

export function activeNavItem<T extends Pick<NavItem, 'to'>>(items: T[], pathname: string) {
  return items.find((item) => navItemIsActive(item, pathname))
}

export function useUnreadCount(): number {
  const trpc = useTRPC()
  const { can } = useSession()

  const { data } = useQuery({
    ...trpc.inbox.unreadCount.queryOptions(),
    enabled: can('INBOX_VIEW'),
  })

  return data ?? 0
}
